import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { indianStates } from '../Components/states';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    countryCode: '+91',
    maritalStatus: '',
    designationName: '',
    addresses: [],
     password: ''
  });
  const [errors, setErrors] = useState({});
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    permanent: {
      address1: '',
      address2: '',
      country: 'India',
      state: 'Tamil Nadu',
      city: '',
      pincode: ''
    },
    present: {
      address1: '',
      address2: '',
      country: 'India',
      state: 'Tamil Nadu',
      city: '',
      pincode: ''
    },
    sameAsPermanent: false
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [activeStateDropdown, setActiveStateDropdown] = useState(null); // 'permanent' or 'present'
  const stateDropdownRef = useRef(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [activeCountryDropdown, setActiveCountryDropdown] = useState(null); // 'permanent' or 'present'
  const countryDropdownRef = useRef(null);

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordRulesVisible, setPasswordRulesVisible] = useState(false);

  const countries = ['India', 'USA', 'UK'];

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
        setStateSearchTerm('');
        setActiveStateDropdown(null);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
        setActiveCountryDropdown(null);
      }
    };

    if (showStateDropdown || showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStateDropdown, showCountryDropdown]);

  const fetchProfile = async () => {
    const userId = localStorage.getItem('id');
    
    if (!userId) {
      toast.error('User ID not found. Please login again.');
      setLoading(false);
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'get-profile',
        id: userId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.profile) {
        const profile = result.profile;
        setFormData({
          firstName: profile.firstName || '',
          middleName: profile.middleName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          mobileNumber: profile.mobileNumber || '',
          countryCode: profile.countryCode || '+91',
          maritalStatus: profile.maritalStatus || '',
          designationName: profile.designationName || '',
          addresses: profile.addresses || [],
          password: ''
        });
      } else {
        toast.error(result?.message || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to prepare addresses for API
  const prepareAddresses = () => {
    const addresses = [];

    formData.addresses.forEach((address) => {
      const addressData = {
        addressType: address.addressType,
        address1: address.address1,
        address2: address.address2 || '',
        country: address.country || 'India',
        state: address.state,
        city: address.city,
        pincode: address.pincode
      };
      addresses.push(addressData);
    });

    return addresses;
  };
  const passwordRules = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "At least one number", test: (p) => /[0-9]/.test(p) },
  { label: "At least one special character", test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const isPasswordValid = (password) => {
  return passwordRules.every(rule => rule.test(password));
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      mobileNumber: numericValue
    }));
    // Clear error when user starts typing
    if (errors.mobileNumber) {
      setErrors(prev => ({
        ...prev,
        mobileNumber: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }
    if (!formData.mobileNumber || formData.mobileNumber.length !== 10) {
      newErrors.mobileNumber = 'Valid 10-digit mobile number is required';
    }
    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital Status is required';
    }

    if (showPasswordChange) {

  if (!formData.password || !confirmPassword) {
    toast.error("Please enter password and confirm password");
    return false;
  }

  if (formData.password !== confirmPassword) {
    toast.error("Passwords do not match");
    return false;
  }

  if (!isPasswordValid(formData.password)) {
    toast.error("Password does not meet the required criteria");
    return false;
  }
}

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!formData.addresses || formData.addresses.length === 0) {
      toast.error('Please add at least one address');
      return;
    }

     if (showPasswordChange) {

    if (!formData.password || !confirmPassword) {
      setPasswordError('Please enter new password and confirm password');
      return;
    }


    if (formData.password.trim() !== confirmPassword.trim()) {
      setPasswordError('Passwords do not match');
      return;
    }


    if (!isPasswordValid(formData.password)) {
      setPasswordError('Password does not meet all requirements');
      return;
    }

  }


  setPasswordError('');


    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    const userId = localStorage.getItem('id');
    if (!userId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        action: 'update-employee-profile',
        id: userId,
        firstName: formData.firstName.trim(),
        middleName: formData.middleName ? formData.middleName.trim() : '',
        lastName: formData.lastName.trim(),
        mobileNumber: formData.mobileNumber,
        maritalStatus: formData.maritalStatus,
        email: formData.email.trim(), // Include email in payload (read-only in UI but sent to API)
        ...(formData.countryCode && { countryCode: formData.countryCode }),
        addresses: prepareAddresses(),
         ...(showPasswordChange && {
        password: formData.password.trim()
      })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.profile) {
        // Update formData with the returned profile data
        const profile = result.profile;
        setFormData({
          firstName: profile.firstName || '',
          middleName: profile.middleName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          mobileNumber: profile.mobileNumber || '',
          countryCode: profile.countryCode || '+91',
          maritalStatus: profile.maritalStatus || '',
          designationName: profile.designationName || '',
          addresses: profile.addresses || [],
           password: ''
        });
         setConfirmPassword('');
      setShowPasswordChange(false);
        toast.success(result.message || 'Profile updated successfully!');
      } else {
        const errorMessage = result?.message || 'Failed to update profile. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      let errorMessage = 'Something went wrong. Please try again.';

      if (error.response) {
        const responseData = error.response.data;
        errorMessage = responseData?.message || `Error: ${error.response.status} ${error.response.statusText}`;

        if (error.response.status === 409) {
          errorMessage = responseData?.message || 'Profile update failed';
        } else if (error.response.status === 400) {
          errorMessage = responseData?.message || 'Invalid data. Please check all fields.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = () => {
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setShowStateDropdown(false);
    setStateSearchTerm('');
    setActiveStateDropdown(null);
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setActiveCountryDropdown(null);
    setAddressData({
      permanent: {
        address1: '',
        address2: '',
        country: 'India',
        state: 'Tamil Nadu',
        city: '',
        pincode: ''
      },
      present: {
        address1: '',
        address2: '',
        country: 'India',
        state: 'Tamil Nadu',
        city: '',
        pincode: ''
      },
      sameAsPermanent: false
    });
    setAddressErrors({});
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'sameAsPermanent') {
      setAddressData(prev => ({
        ...prev,
        sameAsPermanent: checked,
        // If checked, copy permanent address to present address
        ...(checked && {
          present: { ...prev.permanent }
        })
      }));
    } else if (name.startsWith('permanent.')) {
      const field = name.split('.')[1];
      setAddressData(prev => ({
        ...prev,
        permanent: {
          ...prev.permanent,
          [field]: value
        },
        // If sameAsPermanent is checked, update present address too
        ...(prev.sameAsPermanent && {
          present: {
            ...prev.present,
            [field]: value
          }
        })
      }));
    } else if (name.startsWith('present.')) {
      const field = name.split('.')[1];
      setAddressData(prev => ({
        ...prev,
        present: {
          ...prev.present,
          [field]: value
        }
      }));
    } else {
      setAddressData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    const errorKey = name.includes('.') ? name.split('.')[1] : name;
    if (addressErrors[errorKey]) {
      setAddressErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const handleStateSelect = (state, addressType) => {
    setAddressData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        state: state
      },
      // If sameAsPermanent is checked and we're updating permanent, update present too
      ...(addressType === 'permanent' && prev.sameAsPermanent && {
        present: {
          ...prev.present,
          state: state
        }
      })
    }));
    setShowStateDropdown(false);
    setStateSearchTerm('');
    setActiveStateDropdown(null);
    if (addressErrors[`${addressType}.state`]) {
      setAddressErrors(prev => ({
        ...prev,
        [`${addressType}.state`]: ''
      }));
    }
  };

  const handleStateKeyDown = (e, addressType) => {
    if (e.key === 'Enter' && filteredStates.length > 0) {
      e.preventDefault();
      handleStateSelect(filteredStates[0], addressType);
    } else if (e.key === 'Escape') {
      setShowStateDropdown(false);
      setStateSearchTerm('');
      setActiveStateDropdown(null);
    } else if (e.key === 'Tab') {
      setShowStateDropdown(false);
      setStateSearchTerm('');
      setActiveStateDropdown(null);
    }
  };

  const handleCountrySelect = (country, addressType) => {
    setAddressData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        country: country
      },
      // If sameAsPermanent is checked and we're updating permanent, update present too
      ...(addressType === 'permanent' && prev.sameAsPermanent && {
        present: {
          ...prev.present,
          country: country
        }
      })
    }));
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setActiveCountryDropdown(null);
    if (addressErrors[`${addressType}.country`]) {
      setAddressErrors(prev => ({
        ...prev,
        [`${addressType}.country`]: ''
      }));
    }
  };

  const handleCountryKeyDown = (e, addressType) => {
    if (e.key === 'Enter' && filteredCountries.length > 0) {
      e.preventDefault();
      handleCountrySelect(filteredCountries[0], addressType);
    } else if (e.key === 'Escape') {
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
      setActiveCountryDropdown(null);
    } else if (e.key === 'Tab') {
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
      setActiveCountryDropdown(null);
    }
  };

  const filteredStates = indianStates.filter(state =>
    state.toLowerCase().includes(stateSearchTerm.toLowerCase())
  );

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const handlePincodeChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    // Only allow numeric characters and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);

    if (name.startsWith('permanent.')) {
      setAddressData(prev => ({
        ...prev,
        permanent: {
          ...prev.permanent,
          pincode: numericValue
        },
        // If sameAsPermanent is checked, update present too
        ...(prev.sameAsPermanent && {
          present: {
            ...prev.present,
            pincode: numericValue
          }
        })
      }));
    } else if (name.startsWith('present.')) {
      setAddressData(prev => ({
        ...prev,
        present: {
          ...prev.present,
          pincode: numericValue
        }
      }));
    }

    const errorKey = name.includes('.') ? name.split('.')[1] : name;
    if (addressErrors[errorKey]) {
      setAddressErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();

    // Validation for permanent address
    const newErrors = {};
    if (!addressData.permanent.address1.trim()) {
      newErrors['permanent.address1'] = 'Permanent Address 1 is required';
    }
    if (!addressData.permanent.country) {
      newErrors['permanent.country'] = 'Permanent Country is required';
    }
    if (!addressData.permanent.state) {
      newErrors['permanent.state'] = 'Permanent State is required';
    }
    if (!addressData.permanent.city.trim()) {
      newErrors['permanent.city'] = 'Permanent City is required';
    }
    if (!addressData.permanent.pincode || addressData.permanent.pincode.length !== 6) {
      newErrors['permanent.pincode'] = 'Valid 6-digit pincode is required for permanent address';
    }

    // Validation for present address (only if not same as permanent)
    if (!addressData.sameAsPermanent) {
      if (!addressData.present.address1.trim()) {
        newErrors['present.address1'] = 'Present Address 1 is required';
      }
      if (!addressData.present.country) {
        newErrors['present.country'] = 'Present Country is required';
      }
      if (!addressData.present.state) {
        newErrors['present.state'] = 'Present State is required';
      }
      if (!addressData.present.city.trim()) {
        newErrors['present.city'] = 'Present City is required';
      }
      if (!addressData.present.pincode || addressData.present.pincode.length !== 6) {
        newErrors['present.pincode'] = 'Valid 6-digit pincode is required for present address';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setAddressErrors(newErrors);
      toast.error('Please fill all required fields');
      return false;
    }

    // Prepare permanent address
    const permanentAddress = {
      address1: addressData.permanent.address1,
      address2: addressData.permanent.address2 || '',
      country: addressData.permanent.country,
      state: addressData.permanent.state,
      city: addressData.permanent.city,
      pincode: addressData.permanent.pincode,
      addressType: 'permanent',
      id: Date.now(),
      updatedAt: Date.now()
    };

    // Prepare present address
    const presentAddress = {
      address1: addressData.sameAsPermanent ? addressData.permanent.address1 : addressData.present.address1,
      address2: addressData.sameAsPermanent ? addressData.permanent.address2 : (addressData.present.address2 || ''),
      country: addressData.sameAsPermanent ? addressData.permanent.country : addressData.present.country,
      state: addressData.sameAsPermanent ? addressData.permanent.state : addressData.present.state,
      city: addressData.sameAsPermanent ? addressData.permanent.city : addressData.present.city,
      pincode: addressData.sameAsPermanent ? addressData.permanent.pincode : addressData.present.pincode,
      addressType: 'present',
      id: Date.now() + 1,
      updatedAt: Date.now()
    };

    // Replace addresses with only permanent and present addresses
    setFormData(prev => ({
      ...prev,
      addresses: [permanentAddress, presentAddress]
    }));

    toast.success('Permanent and Present addresses added successfully!');
    handleCloseAddressModal();
    return true;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getPermanentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'permanent');
  };

  const getPresentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'present');
  };

  const handleEditAddress = () => {
    const permanentAddr = getPermanentAddress();
    const presentAddr = getPresentAddress();
    
    if (permanentAddr) {
      setAddressData(prev => ({
        ...prev,
        permanent: {
          address1: permanentAddr.address1 || '',
          address2: permanentAddr.address2 || '',
          country: permanentAddr.country || 'India',
          state: permanentAddr.state || 'Tamil Nadu',
          city: permanentAddr.city || '',
          pincode: permanentAddr.pincode || ''
        }
      }));
    }
    
    if (presentAddr) {
      const isSameAsPermanent = permanentAddr && 
        permanentAddr.address1 === presentAddr.address1 &&
        permanentAddr.city === presentAddr.city &&
        permanentAddr.pincode === presentAddr.pincode;
      
      setAddressData(prev => ({
        ...prev,
        present: {
          address1: presentAddr.address1 || '',
          address2: presentAddr.address2 || '',
          country: presentAddr.country || 'India',
          state: presentAddr.state || 'Tamil Nadu',
          city: presentAddr.city || '',
          pincode: presentAddr.pincode || ''
        },
        sameAsPermanent: isSameAsPermanent
      }));
    }
    
    setShowAddressModal(true);
  };

  if (loading) {
    return (
      <div className="employee-form">
        <section className="content">
          <div className="container-fluid">
            <Loading size={180} message="Loading profile..." />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                My Profile
                <i className="fas fa-user ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View and update your personal information and address details.
              </p>
            </div>
          </div>

          {/* Profile Form Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div className="mb-4">
                  <h4 className="section-title">Personal Information</h4>
                  <p className="section-subtitle">
                    Update your personal details
                  </p>

                  <div className="form-row-3">
                    <div className="form-group">
                      <label htmlFor="firstName" className="form-label">
                        First Name<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && (
                        <span className="error-message">{errors.firstName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="middleName" className="form-label">Middle Name</label>
                      <input
                        type="text"
                        id="middleName"
                        name="middleName"
                        className="form-control"
                        value={formData.middleName}
                        onChange={handleChange}
                        placeholder="Enter middle name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName" className="form-label">
                        Last Name<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && (
                        <span className="error-message">{errors.lastName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                      <small className="form-text text-muted">Email cannot be changed</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="designationName" className="form-label">
                        Designation
                      </label>
                      <input
                        type="text"
                        id="designationName"
                        name="designationName"
                        className="form-control"
                        value={formData.designationName}
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                      <small className="form-text text-muted">Designation is assigned by the organization</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number<span className="required-star">*</span>
                      </label>
                      <div className="mobile-input-group">
                        <span className="country-code-prefix">+91</span>
                        <input
                          type="tel"
                          id="mobileNumber"
                          name="mobileNumber"
                          className={`form-control mobile-input ${errors.mobileNumber ? 'is-invalid' : ''}`}
                          value={formData.mobileNumber}
                          onChange={handleMobileNumberChange}
                          placeholder="Enter 10 digit mobile number"
                          maxLength="10"
                          inputMode="numeric"
                        />
                      </div>
                      {errors.mobileNumber && (
                        <span className="error-message">{errors.mobileNumber}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="maritalStatus" className="form-label">
                        Marital Status<span className="required-star">*</span>
                      </label>
                      <select
                        id="maritalStatus"
                        name="maritalStatus"
                        className={`form-control ${errors.maritalStatus ? 'is-invalid' : ''}`}
                        value={formData.maritalStatus}
                        onChange={handleChange}
                      >
                        <option value="">Select Marital Status</option>
                        <option value="unmarried">Unmarried</option>
                        <option value="married">Married</option>
                      </select>
                      {errors.maritalStatus && (
                        <span className="error-message">{errors.maritalStatus}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="section-divider">
                  <div className="section-header">
                    <div className="section-header-content">
                      <h4>Address</h4>
                      <p>Your permanent & present address</p>
                    </div>
                    <button
                      type="button"
                      onClick={getPermanentAddress() || getPresentAddress() ? handleEditAddress : handleAddAddress}
                      className="btn-add btn-enabled"
                    >
                      {getPermanentAddress() || getPresentAddress() ? 'Edit' : 'Add'}
                    </button>
                  </div>

                  {/* Display Saved Addresses - Side by Side */}
                  {(getPermanentAddress() || getPresentAddress()) && (
                    <div className="address-list address-list-spacing">
                      {/* Permanent Address Card */}
                      <div className="address-card">
                        <div className="address-card-header">
                          <h5 className="address-card-title">
                            Permanent Address
                          </h5>
                        </div>
                        {getPermanentAddress() ? (
                          <>
                            <div className="address-content">
                              <p>{getPermanentAddress().address1}</p>
                              {getPermanentAddress().address2 && <p>{getPermanentAddress().address2}</p>}
                              <p>{getPermanentAddress().city}, {getPermanentAddress().state}</p>
                              <p>{getPermanentAddress().country} - {getPermanentAddress().pincode}</p>
                            </div>
                            <p className="address-update-text">
                              Last update on {formatDate(getPermanentAddress().updatedAt || getPermanentAddress().id)}
                            </p>
                          </>
                        ) : (
                          <div className="address-empty-state">
                            No permanent address
                          </div>
                        )}
                      </div>

                      {/* Present Address Card */}
                      <div className="address-card">
                        <div className="address-card-header">
                          <h5 className="address-card-title">
                            Present Address
                          </h5>
                        </div>
                        {getPresentAddress() ? (
                          <>
                            <div className="address-content">
                              <p>{getPresentAddress().address1}</p>
                              {getPresentAddress().address2 && <p>{getPresentAddress().address2}</p>}
                              <p>{getPresentAddress().city}, {getPresentAddress().state}</p>
                              <p>{getPresentAddress().country} - {getPresentAddress().pincode}</p>
                            </div>
                            <p className="address-update-text">
                              Last update on {formatDate(getPresentAddress().updatedAt || getPresentAddress().id)}
                            </p>
                          </>
                        ) : (
                          <div className="address-empty-state">
                            No present address
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(!getPermanentAddress() && !getPresentAddress()) && (
                    <div className="address-empty-container">
                      <i className="fas fa-map-marker-alt address-empty-icon"></i>
                      No addresses added yet. Click "Add" to add an address.
                    </div>
                  )}
                </div>

             {/* Password Section */}
             <div
                  className="form-group"
                  style={{
                    borderTop: "1px solid #e9ecef",
                    paddingTop: "1.5rem",
                    marginTop: "1.5rem",
                  }}
                >
                  <label
                    className="form-label"
                    style={{ fontWeight: 500, fontSize: "15px" }}
                  >
                    Password
                  </label>

                  <div style={{ marginTop: "4px" }}>
                    <a
                      href="#"
                      className="f_cream change_pwdclk crsr_pntr"
                      style={{
                        fontSize: "14px",
                        color: "#007bff",
                        textDecoration: "none",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPasswordChange(!showPasswordChange);
                        if (showPasswordChange) {
                          setFormData({ ...formData, password: "" });
                          setConfirmPassword("");
                          setPasswordError("");
                          setShowPassword(false);
                          setShowConfirmPassword(false);
                        }
                      }}
                    >
                      {showPasswordChange
                        ? "Cancel Password Change"
                        : "Change Password"}
                    </a>
                  </div>

                  {showPasswordChange && (
                    <div
                      style={{
                        marginTop: "1.25rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.25rem",
                        maxWidth: "480px",
                      }}
                    >
                      {/* New Password */}
                      <div className="form-group mb-0">
                        <label
                          className="form-label"
                          style={{ fontSize: "13px", fontWeight: 500 }}
                        >
                          New Password<span className="required-star">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            className="form-control"
                            placeholder="Enter new password"
                            value={formData.password}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                password: e.target.value,
                              });
                              setPasswordError("");
                            }}
                            style={{ paddingRight: "42px" }}
                          />
                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: "#6c757d",
                              zIndex: 10,
                              userSelect: "none",
                            }}
                          >
                            <i
                              className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                            />
                          </span>
                        </div>

                        {formData.password &&
                          (() => {
                            const passedCount = passwordRules.filter((r) =>
                              r.test(formData.password),
                            ).length;
                            const strengthColor =
                              passedCount <= 2
                                ? "#dc3545"
                                : passedCount <= 4
                                  ? "#ffc107"
                                  : "#28a745";
                            const strengthLabel =
                              passedCount <= 2
                                ? "Weak"
                                : passedCount <= 4
                                  ? "Medium"
                                  : "Strong";
                            return (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    marginTop: "8px",
                                    alignItems: "center",
                                  }}
                                >
                                  {passwordRules.map((_, i) => (
                                    <div
                                      key={i}
                                      style={{
                                        flex: 1,
                                        height: "4px",
                                        borderRadius: "2px",
                                        background:
                                          i < passedCount
                                            ? strengthColor
                                            : "#dee2e6",
                                        transition: "background 0.2s",
                                      }}
                                    />
                                  ))}
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      color: strengthColor,
                                      marginLeft: "8px",
                                      minWidth: "45px",
                                      textAlign: "right",
                                    }}
                                  >
                                    {strengthLabel}
                                  </span>
                                </div>

                                <div
                                  style={{
                                    marginTop: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "5px",
                                    padding: "10px 12px",
                                    background: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "1px solid #e9ecef",
                                  }}
                                >
                                  {passwordRules.map((rule, i) => {
                                    const passed = rule.test(formData.password);
                                    return (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: "12px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "7px",
                                          color: passed ? "#28a745" : "#6c757d",
                                        }}
                                      >
                                        <i
                                          className={`fas ${passed ? "fa-check-circle" : "fa-circle"}`}
                                          style={{
                                            fontSize: "12px",
                                            flexShrink: 0,
                                          }}
                                        />
                                        {rule.label}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                      </div>

                      {/* Confirm Password */}
                      <div className="form-group mb-0">
                        <label
                          className="form-label"
                          style={{ fontSize: "13px", fontWeight: 500 }}
                        >
                          Confirm New Password
                          <span className="required-star">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            className="form-control"                            
                            placeholder="Re-enter new password"
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              setPasswordError("");
                            }}
                            style={{ paddingRight: "42px" }}
                          />
                          <span
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: "#6c757d",
                              zIndex: 10,
                              userSelect: "none",
                            }}
                          >
                            <i
                              className={`fas ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                            />
                          </span>
                        </div>

                        {/* Match feedback */}
                        {confirmPassword.length > 0 &&
                          ((formData.password || "") === confirmPassword ? (
                            <small
                              style={{
                                color: "#28a745",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginTop: "5px",
                              }}
                            >
                              <i className="fas fa-check-circle" />
                              Passwords match
                            </small>
                          ) : (
                            <small
                              style={{
                                color: "#dc3545",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                marginTop: "5px",
                              }}
                            >
                              <i className="fas fa-times-circle" />
                              Passwords do not match
                            </small>
                          ))}

                        {passwordError && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "13px" }}
                          >
                            {passwordError}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Submit Button */}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={handleCloseAddressModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Address</h5>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseAddressModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <form onSubmit={handleSaveAddress}>
                {/* Permanent Address Section */}
                <div className="mb-4">
                  <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                    Permanent Address
                  </h6>

                  {/* Address 1 and Address 2 - Side by Side */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.address1" className="form-label">
                          Address 1<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.address1"
                          name="permanent.address1"
                          className={`form-control ${addressErrors['permanent.address1'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.address1}
                          onChange={handleAddressChange}
                          placeholder="Enter address line 1"
                        />
                        {addressErrors['permanent.address1'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.address1']}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.address2" className="form-label">Address 2</label>
                        <input
                          type="text"
                          id="permanent.address2"
                          name="permanent.address2"
                          className="form-control"
                          value={addressData.permanent.address2}
                          onChange={handleAddressChange}
                          placeholder="Enter address line 2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Country and State - Side by Side */}
                  <div className="row">
                    <div className={`col-md-6 ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'modal-col-z-index-9999' : (showStateDropdown && activeStateDropdown === 'permanent' ? 'modal-col-z-index-1' : 'modal-col-z-index-auto')}`}>
                      <div className="form-group">
                        <label htmlFor="permanent.country" className="form-label">
                          Country<span className="required-star">*</span>
                        </label>
                        <div className={`custom-dropdown-wrapper ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'active' : ''}`} ref={countryDropdownRef}>
                          <div
                            className={`custom-dropdown ${addressErrors['permanent.country'] ? 'is-invalid' : ''} ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'active' : ''}`}
                            onClick={() => {
                              if (!showCountryDropdown || activeCountryDropdown !== 'permanent') {
                                setShowCountryDropdown(true);
                                setActiveCountryDropdown('permanent');
                                setCountrySearchTerm('');
                              }
                            }}
                            tabIndex={0}
                            onKeyDown={showCountryDropdown && activeCountryDropdown === 'permanent' ? undefined : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowCountryDropdown(true);
                                setActiveCountryDropdown('permanent');
                                setCountrySearchTerm('');
                              }
                            }}
                          >
                            {showCountryDropdown && activeCountryDropdown === 'permanent' ? (
                              <input
                                type="text"
                                className="custom-dropdown-input"
                                value={countrySearchTerm}
                                onChange={(e) => {
                                  setCountrySearchTerm(e.target.value);
                                }}
                                onKeyDown={(e) => handleCountryKeyDown(e, 'permanent')}
                                placeholder="Search country..."
                                autoFocus
                              />
                            ) : (
                              <div className="custom-dropdown-display" tabIndex={-1}>
                                {addressData.permanent.country || 'Select Country'}
                              </div>
                            )}
                            <i
                              className={`fas fa-chevron-${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'up' : 'down'} custom-dropdown-arrow`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showCountryDropdown && activeCountryDropdown === 'permanent') {
                                  setShowCountryDropdown(false);
                                  setActiveCountryDropdown(null);
                                } else {
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('permanent');
                                  setCountrySearchTerm('');
                                }
                              }}
                            ></i>
                          </div>
                          {showCountryDropdown && activeCountryDropdown === 'permanent' && (
                            <div className="custom-dropdown-list">
                              <div className="custom-dropdown-options">
                                {filteredCountries.length > 0 ? (
                                  filteredCountries.map((country) => (
                                    <div
                                      key={country}
                                      className={`custom-dropdown-option ${addressData.permanent.country === country ? 'selected' : ''}`}
                                      onClick={() => handleCountrySelect(country, 'permanent')}
                                    >
                                      {country}
                                    </div>
                                  ))
                                ) : (
                                  <div className="custom-dropdown-option no-results">No countries found</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {addressErrors['permanent.country'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.country']}</div>
                        )}
                      </div>
                    </div>
                    <div className={`col-md-6 ${showStateDropdown && activeStateDropdown === 'permanent' ? 'modal-col-z-index-9999' : 'modal-col-z-index-auto'}`}>
                      <div className="form-group">
                        <label htmlFor="permanent.state" className="form-label">
                          State<span className="required-star">*</span>
                        </label>
                        <div className={`custom-dropdown-wrapper ${showStateDropdown && activeStateDropdown === 'permanent' ? 'active' : ''}`} ref={stateDropdownRef}>
                          <div
                            className={`custom-dropdown ${addressErrors['permanent.state'] ? 'is-invalid' : ''} ${showStateDropdown && activeStateDropdown === 'permanent' ? 'active' : ''}`}
                            onClick={() => {
                              if (!showStateDropdown || activeStateDropdown !== 'permanent') {
                                setShowStateDropdown(true);
                                setActiveStateDropdown('permanent');
                                setStateSearchTerm('');
                              }
                            }}
                            tabIndex={0}
                            onKeyDown={showStateDropdown && activeStateDropdown === 'permanent' ? undefined : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowStateDropdown(true);
                                setActiveStateDropdown('permanent');
                                setStateSearchTerm('');
                              }
                            }}
                          >
                            {showStateDropdown && activeStateDropdown === 'permanent' ? (
                              <input
                                type="text"
                                className="custom-dropdown-input"
                                value={stateSearchTerm}
                                onChange={(e) => {
                                  setStateSearchTerm(e.target.value);
                                }}
                                onKeyDown={(e) => handleStateKeyDown(e, 'permanent')}
                                placeholder="Search state..."
                                autoFocus
                              />
                            ) : (
                              <div className="custom-dropdown-display" tabIndex={-1}>
                                {addressData.permanent.state || 'Select State'}
                              </div>
                            )}
                            <i
                              className={`fas fa-chevron-${showStateDropdown && activeStateDropdown === 'permanent' ? 'up' : 'down'} custom-dropdown-arrow`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showStateDropdown && activeStateDropdown === 'permanent') {
                                  setShowStateDropdown(false);
                                  setActiveStateDropdown(null);
                                } else {
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('permanent');
                                  setStateSearchTerm('');
                                }
                              }}
                            ></i>
                          </div>
                          {showStateDropdown && activeStateDropdown === 'permanent' && (
                            <div className="custom-dropdown-list">
                              <div className="custom-dropdown-options">
                                {filteredStates.length > 0 ? (
                                  filteredStates.map((state) => (
                                    <div
                                      key={state}
                                      className={`custom-dropdown-option ${addressData.permanent.state === state ? 'selected' : ''}`}
                                      onClick={() => handleStateSelect(state, 'permanent')}
                                    >
                                      {state}
                                    </div>
                                  ))
                                ) : (
                                  <div className="custom-dropdown-option no-results">No states found</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {addressErrors['permanent.state'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.state']}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* City and Pincode - Side by Side */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.city" className="form-label">
                          City<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.city"
                          name="permanent.city"
                          className={`form-control ${addressErrors['permanent.city'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.city}
                          onChange={handleAddressChange}
                          placeholder="Enter city"
                        />
                        {addressErrors['permanent.city'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.city']}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.pincode" className="form-label">
                          Pincode<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.pincode"
                          name="permanent.pincode"
                          className={`form-control ${addressErrors['permanent.pincode'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.pincode}
                          onChange={handlePincodeChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          inputMode="numeric"
                        />
                        {addressErrors['permanent.pincode'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.pincode']}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checkbox for same as permanent */}
                <div className="form-group mt-3 mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="sameAsPermanent"
                      name="sameAsPermanent"
                      className="form-check-input"
                      checked={addressData.sameAsPermanent}
                      onChange={handleAddressChange}
                    />
                    <label className="form-check-label" htmlFor="sameAsPermanent">
                      Present address same as Permanent address
                    </label>
                  </div>
                </div>

                {/* Present Address Section - Hidden when sameAsPermanent is checked */}
                {!addressData.sameAsPermanent && (
                  <div className="mb-4">
                    <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                      Present Address
                    </h6>

                    {/* Address 1 and Address 2 - Side by Side */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.address1" className="form-label">
                            Address 1<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.address1"
                            name="present.address1"
                            className={`form-control ${addressErrors['present.address1'] ? 'is-invalid' : ''}`}
                            value={addressData.present.address1}
                            onChange={handleAddressChange}
                            placeholder="Enter address line 1"
                          />
                          {addressErrors['present.address1'] && (
                            <div className="invalid-feedback">{addressErrors['present.address1']}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.address2" className="form-label">Address 2</label>
                          <input
                            type="text"
                            id="present.address2"
                            name="present.address2"
                            className="form-control"
                            value={addressData.present.address2}
                            onChange={handleAddressChange}
                            placeholder="Enter address line 2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Country and State - Side by Side */}
                    <div className="row">
                      <div className={`col-md-6 ${showCountryDropdown && activeCountryDropdown === 'present' ? 'modal-col-z-index-9999' : (showStateDropdown && activeStateDropdown === 'present' ? 'modal-col-z-index-1' : 'modal-col-z-index-auto')}`}>
                        <div className="form-group">
                          <label htmlFor="present.country" className="form-label">
                            Country<span className="required-star">*</span>
                          </label>
                          <div className={`custom-dropdown-wrapper ${showCountryDropdown && activeCountryDropdown === 'present' ? 'active' : ''}`} ref={countryDropdownRef}>
                            <div
                              className={`custom-dropdown ${addressErrors['present.country'] ? 'is-invalid' : ''} ${showCountryDropdown && activeCountryDropdown === 'present' ? 'active' : ''}`}
                              onClick={() => {
                                if (!showCountryDropdown || activeCountryDropdown !== 'present') {
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('present');
                                  setCountrySearchTerm('');
                                }
                              }}
                              tabIndex={0}
                              onKeyDown={showCountryDropdown && activeCountryDropdown === 'present' ? undefined : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('present');
                                  setCountrySearchTerm('');
                                }
                              }}
                            >
                              {showCountryDropdown && activeCountryDropdown === 'present' ? (
                                <input
                                  type="text"
                                  className="custom-dropdown-input"
                                  value={countrySearchTerm}
                                  onChange={(e) => {
                                    setCountrySearchTerm(e.target.value);
                                  }}
                                  onKeyDown={(e) => handleCountryKeyDown(e, 'present')}
                                  placeholder="Search country..."
                                  autoFocus
                                />
                              ) : (
                                <div className="custom-dropdown-display" tabIndex={-1}>
                                  {addressData.present.country || 'Select Country'}
                                </div>
                              )}
                              <i
                                className={`fas fa-chevron-${showCountryDropdown && activeCountryDropdown === 'present' ? 'up' : 'down'} custom-dropdown-arrow`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (showCountryDropdown && activeCountryDropdown === 'present') {
                                    setShowCountryDropdown(false);
                                    setActiveCountryDropdown(null);
                                  } else {
                                    setShowCountryDropdown(true);
                                    setActiveCountryDropdown('present');
                                    setCountrySearchTerm('');
                                  }
                                }}
                              ></i>
                            </div>
                            {showCountryDropdown && activeCountryDropdown === 'present' && (
                              <div className="custom-dropdown-list">
                                <div className="custom-dropdown-options">
                                  {filteredCountries.length > 0 ? (
                                    filteredCountries.map((country) => (
                                      <div
                                        key={country}
                                        className={`custom-dropdown-option ${addressData.present.country === country ? 'selected' : ''}`}
                                        onClick={() => handleCountrySelect(country, 'present')}
                                      >
                                        {country}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="custom-dropdown-option no-results">No countries found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {addressErrors['present.country'] && (
                            <div className="invalid-feedback">{addressErrors['present.country']}</div>
                          )}
                        </div>
                      </div>
                      <div className={`col-md-6 ${showStateDropdown && activeStateDropdown === 'present' ? 'modal-col-z-index-9999' : 'modal-col-z-index-auto'}`}>
                        <div className="form-group">
                          <label htmlFor="present.state" className="form-label">
                            State<span className="required-star">*</span>
                          </label>
                          <div className={`custom-dropdown-wrapper ${showStateDropdown && activeStateDropdown === 'present' ? 'active' : ''}`} ref={stateDropdownRef}>
                            <div
                              className={`custom-dropdown ${addressErrors['present.state'] ? 'is-invalid' : ''} ${showStateDropdown && activeStateDropdown === 'present' ? 'active' : ''}`}
                              onClick={() => {
                                if (!showStateDropdown || activeStateDropdown !== 'present') {
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('present');
                                  setStateSearchTerm('');
                                }
                              }}
                              tabIndex={0}
                              onKeyDown={showStateDropdown && activeStateDropdown === 'present' ? undefined : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('present');
                                  setStateSearchTerm('');
                                }
                              }}
                            >
                              {showStateDropdown && activeStateDropdown === 'present' ? (
                                <input
                                  type="text"
                                  className="custom-dropdown-input"
                                  value={stateSearchTerm}
                                  onChange={(e) => {
                                    setStateSearchTerm(e.target.value);
                                  }}
                                  onKeyDown={(e) => handleStateKeyDown(e, 'present')}
                                  placeholder="Search state..."
                                  autoFocus
                                />
                              ) : (
                                <div className="custom-dropdown-display" tabIndex={-1}>
                                  {addressData.present.state || 'Select State'}
                                </div>
                              )}
                              <i
                                className={`fas fa-chevron-${showStateDropdown && activeStateDropdown === 'present' ? 'up' : 'down'} custom-dropdown-arrow`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (showStateDropdown && activeStateDropdown === 'present') {
                                    setShowStateDropdown(false);
                                    setActiveStateDropdown(null);
                                  } else {
                                    setShowStateDropdown(true);
                                    setActiveStateDropdown('present');
                                    setStateSearchTerm('');
                                  }
                                }}
                              ></i>
                            </div>
                            {showStateDropdown && activeStateDropdown === 'present' && (
                              <div className="custom-dropdown-list">
                                <div className="custom-dropdown-options">
                                  {filteredStates.length > 0 ? (
                                    filteredStates.map((state) => (
                                      <div
                                        key={state}
                                        className={`custom-dropdown-option ${addressData.present.state === state ? 'selected' : ''}`}
                                        onClick={() => handleStateSelect(state, 'present')}
                                      >
                                        {state}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="custom-dropdown-option no-results">No states found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {addressErrors['present.state'] && (
                            <div className="invalid-feedback">{addressErrors['present.state']}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* City and Pincode - Side by Side */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.city" className="form-label">
                            City<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.city"
                            name="present.city"
                            className={`form-control ${addressErrors['present.city'] ? 'is-invalid' : ''}`}
                            value={addressData.present.city}
                            onChange={handleAddressChange}
                            placeholder="Enter city"
                          />
                          {addressErrors['present.city'] && (
                            <div className="invalid-feedback">{addressErrors['present.city']}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.pincode" className="form-label">
                            Pincode<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.pincode"
                            name="present.pincode"
                            className={`form-control ${addressErrors['present.pincode'] ? 'is-invalid' : ''}`}
                            value={addressData.present.pincode}
                            onChange={handlePincodeChange}
                            placeholder="Enter 6-digit pincode"
                            maxLength="6"
                            inputMode="numeric"
                          />
                          {addressErrors['present.pincode'] && (
                            <div className="invalid-feedback">{addressErrors['present.pincode']}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseAddressModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAddress}
                    className="btn-primary"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
